import { Maths, DEFAULT_ROUND } from 'helpers/maths';
import { calcConversions, unitsAreSame } from 'helpers/unitConversionHelpers';
import { isEqual, isNumber } from 'lodash';
import Decimal from 'decimal.js';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { VolumeAllUnits } from 'helpers/unitsAbbreviation';
import formatNumber from 'helpers/formatNumber';
import { TankInfoFieldNames } from '../../TankInformationSection/types';

// Helper function to check if product is a liquid
export const isLiquidProduct = (unit: string): boolean => {
  return VolumeAllUnits.includes(unit);
};

export const isLiquidWithTankInfo = (
  unit: string,
  isTankInfoEnabled?: boolean
): boolean => {
  return isLiquidProduct(unit) && !!isTankInfoEnabled;
};

export const getNonCarrierLiquidProducts = (products: Product[]): Product[] => {
  return products.filter(p => !p.isCarrier && isLiquidProduct(p.stdunit));
};

// Helper function to compare values with formatted number comparison
export const compareValuesWithFormatting = (
  prevVal: any,
  currVal: any,
  roundedTo: number = 2
): boolean => {
  if (isNumber(prevVal) && isNumber(currVal)) {
    const formattedPrev = formatNumber(prevVal, '', roundedTo);
    const formattedCurr = formatNumber(currVal, '', roundedTo);
    return formattedPrev === formattedCurr;
  }
  return isEqual(prevVal, currVal);
};

// Helper function to check if product should be processed
const shouldProcessProduct = (product: Product): boolean => {
  return isLiquidProduct(product.stdunit) || product.isCarrier;
};

// Helper function to calculate non-carrier product rate per tank
const calculateNonCarrierRatePerTank = (
  product: Product,
  tankCount: number,
  convertFn?: Function
): Product => {
  const ratePerTank = calcConversions(
    Maths.divideSafeRound(product?.totalProduct || 0, tankCount),
    product.totalProductUnit,
    product.ratePerTankUnit,
    product.totalProductUnit === product.ratePerTankUnit,
    product,
    convertFn
  );

  return {
    ...product,
    ratePerTank
  };
};

export const differenceBetweenProducts = (
  previousProducts: Product[],
  currentProducts: Product[]
): { [key: string]: string[] } => {
  const result: { [key: string]: string[] } = {};

  // Helper to get key
  const getKey = (product: Product) =>
    product.productApplicationId ||
    (product.isCarrier ? product.id : undefined);

  // Build a map for current products
  const currentMap = currentProducts.reduce<Map<string, Product>>((map, p) => {
    const key = getKey(p);
    if (key) map.set(key, p);
    return map;
  }, new Map<string, Product>());

  const prevChanges = previousProducts.reduce<Record<string, string[]>>(
    (acc, prev) => {
      const key = getKey(prev);
      if (!key) return acc;

      const curr = currentMap.get(key);
      if (!curr) return acc;

      const allFields = new Set<keyof Product>([
        ...(Object.keys(prev) as Array<keyof Product>),
        ...(Object.keys(curr) as Array<keyof Product>)
      ]);

      const changedFields = Array.from(allFields).reduce<string[]>(
        (fields, field) => {
          if (field === 'id' || field === 'productApplicationId') return fields;

          const prevVal = prev[field];
          const currVal = curr[field];

          // Use formatted comparison for numbers, regular comparison for other types
          if (!compareValuesWithFormatting(prevVal, currVal)) {
            fields.push(field as string);
          }
          return fields;
        },
        []
      );

      acc[key] = changedFields;
      return acc;
    },
    {}
  );

  Object.assign(result, prevChanges);

  // Ensure all current products keys are present
  // eslint-disable-next-line no-restricted-syntax
  for (const product of currentProducts) {
    const key = getKey(product);
    if (key && !(key in result)) {
      result[key] = [];
    }
  }

  return result;
};

export const differenceBetweenApplications = (
  previous: FormValues,
  current: FormValues
): { changedFields: string[]; products: { [key: string]: string[] } } => {
  const excludedFields = new Set<keyof FormValues>(['products']);

  const allFields = new Set<keyof FormValues>([
    ...(Object.keys(previous) as Array<keyof FormValues>),
    ...(Object.keys(current) as Array<keyof FormValues>)
  ]);

  const changedFields = Array.from(allFields).reduce<string[]>((acc, field) => {
    if (excludedFields.has(field)) return acc;
    const prevVal = (previous as any)[field];
    const currVal = (current as any)[field];

    // Use formatted comparison for numbers, regular comparison for other types
    if (!compareValuesWithFormatting(prevVal, currVal)) {
      acc.push(field as string);
    }
    return acc;
  }, []);

  const products = differenceBetweenProducts(
    previous.products || [],
    current.products || []
  );

  return { changedFields, products };
};

export const calculateTotalProductsByKey = (
  products: Product[],
  key: 'ratePerAcre' | 'ratePerTank' | 'totalProduct'
) => {
  return products.reduce((total, product) => {
    const unitFieldMap: Record<typeof key, string> = {
      ratePerAcre: 'ratePerAreaUnit',
      ratePerTank: 'ratePerTankUnit',
      totalProduct: 'totalProductUnit'
    };

    const unit = (product as any)[unitFieldMap[key]] || 'gallon';
    const numericValue = Number(product[key] || 0);

    let converted = numericValue;
    if (
      unit &&
      String(unit).toLowerCase() !== 'gallon' &&
      !Number.isNaN(numericValue)
    ) {
      try {
        converted = calcConversions(
          numericValue,
          unit,
          'gallon',
          unit === 'gallon',
          product
        );
      } catch {
        converted = numericValue;
      }
    }

    return total + (Number.isNaN(converted) ? 0 : converted);
  }, 0);
};

// Helper function to calculate carrier product values
const calculateCarrierProductValues = (
  product: Product,
  application: FormValues,
  overallTotalProduct: number,
  tankCount: number
): Product => {
  const nonCarrierProducts = getNonCarrierLiquidProducts(application.products);
  const sumOfTotalProducts = calculateTotalProductsByKey(
    nonCarrierProducts,
    'totalProduct'
  );
  const carrierTotalProduct = overallTotalProduct - sumOfTotalProducts;

  return {
    ...product,
    totalProduct: carrierTotalProduct,
    ratePerAcre: Maths.divideSafeRound(
      carrierTotalProduct,
      application.totalAppliedAcreage
    ),
    ratePerTank: Maths.divideSafeRound(carrierTotalProduct, tankCount)
  };
};

const recalculateOnTankSizeChange = (
  application: FormValues,
  value: number,
  convertFn?: Function
) => {
  const tankCount = application?.totalProduct
    ? Maths.divideSafeRound(application?.totalProduct, value)
    : 0;

  if (tankCount === 0) {
    return { ...application, tankCount };
  }

  const products = application.products.map(product => {
    if (!shouldProcessProduct(product)) return product;

    const ratePerTank = calcConversions(
      Maths.divideSafeRound(product?.totalProduct || 0, tankCount),
      product.totalProductUnit,
      product.ratePerTankUnit,
      product.totalProductUnit === product.ratePerTankUnit,
      product,
      convertFn
    );

    return {
      ...product,
      ratePerTank
    };
  });

  return {
    ...application,
    products,
    tankCount
  };
};

const recalculateOnTankCountChange = (
  application: FormValues,
  value: number,
  convertFn?: Function
) => {
  const overallTotalProduct = application.tankSize
    ? Maths.multiplyRound(application?.tankSize, value)
    : 0;

  const overallRatePerAcre = Maths.divideSafeRound(
    overallTotalProduct,
    application.totalAppliedAcreage
  );

  const products = application.products.map(product => {
    if (!shouldProcessProduct(product)) return product;

    const totalProductUnit = product.totalProductUnit ?? 'gallon';
    const ratePerAreaUnit = product.ratePerAreaUnit ?? 'gallon';

    const ratePerTankInTotalUnit = calcConversions(
      product.ratePerTank,
      product.ratePerTankUnit,
      totalProductUnit,
      unitsAreSame(product.ratePerTankUnit, totalProductUnit),
      product,
      convertFn
    );

    const totalProduct = Maths.multiplyRound(ratePerTankInTotalUnit, value);

    const ratePerAcre = calcConversions(
      Maths.divideSafeRound(totalProduct, application.totalAppliedAcreage),
      totalProductUnit,
      ratePerAreaUnit,
      unitsAreSame(totalProductUnit, ratePerAreaUnit),
      product,
      convertFn
    );

    return {
      ...product,
      ratePerAcre,
      totalProduct
    };
  });

  return {
    ...application,
    totalProduct: overallTotalProduct,
    overallRatePerAcre,
    products
  };
};

const recalculateOnOverallRatePerAcreChange = (
  application: FormValues,
  value: number,
  convertFn?: Function
) => {
  const overallTotalProduct = application.totalAppliedAcreage * value;
  const tankCount = application.tankSize
    ? Maths.divideSafeRound(overallTotalProduct, application.tankSize)
    : 0;

  const products = application.products.map(product => {
    if (!shouldProcessProduct(product)) return product;

    if (product.isCarrier) {
      return calculateCarrierProductValues(
        product,
        application,
        overallTotalProduct,
        tankCount
      );
    }

    return calculateNonCarrierRatePerTank(product, tankCount, convertFn);
  });

  return {
    ...application,
    tankCount,
    totalProduct: overallTotalProduct,
    products
  };
};

const recalculateOnTotalProductChange = (
  application: FormValues,
  value: number,
  convertFn?: Function
) => {
  const overallRatePerAcre = Maths.divideSafeRound(
    value,
    application.totalAppliedAcreage
  );
  const tankCount = application.tankSize
    ? Maths.divideSafeRound(value, application.tankSize)
    : 0;

  const products = application.products.map(product => {
    if (!shouldProcessProduct(product)) return product;

    if (product.isCarrier) {
      return calculateCarrierProductValues(
        product,
        application,
        value,
        tankCount
      );
    }

    return calculateNonCarrierRatePerTank(product, tankCount, convertFn);
  });

  return {
    ...application,
    overallRatePerAcre,
    tankCount,
    products
  };
};

export const recalculateApplicationValues = (
  application: FormValues,
  changedField: TankInfoFieldNames,
  value: number,
  convertFn?: Function
): FormValues => {
  const recalculateFns = {
    tankSize: recalculateOnTankSizeChange,
    tankCount: recalculateOnTankCountChange,
    overallRatePerAcre: recalculateOnOverallRatePerAcreChange,
    totalProduct: recalculateOnTotalProductChange
  };

  const recalculateFn = recalculateFns[changedField];

  if (!recalculateFn) return application;

  return recalculateFn(application, value, convertFn);
};

// Helper function to get initial values based on the changed field
const getInitialValues = (
  application: FormValues,
  changedField: TankInfoFieldNames,
  value: number
) => {
  return {
    overallTotalProduct:
      changedField === 'totalProduct' ? value : application.totalProduct,
    overallRatePerAcre:
      changedField === 'overallRatePerAcre'
        ? value
        : application.overallRatePerAcre,
    tankCount: changedField === 'tankCount' ? value : application.tankCount,
    tankSize: changedField === 'tankSize' ? value : application.tankSize
  };
};

// Helper function to calculate overall total product
const calculateOverallTotalProduct = (
  changedField: string,
  initialValues: ReturnType<typeof getInitialValues>,
  application: FormValues
): number => {
  if (
    changedField !== 'totalProduct' &&
    initialValues.tankSize &&
    initialValues.tankCount
  ) {
    return Maths.multiplyRound(initialValues.tankSize, initialValues.tankCount);
  }

  if (
    changedField !== 'totalProduct' &&
    initialValues.overallRatePerAcre &&
    application.totalAppliedAcreage
  ) {
    return Maths.multiplyRound(
      initialValues.overallRatePerAcre,
      application.totalAppliedAcreage
    );
  }

  return initialValues.overallTotalProduct || 0;
};

// Helper function to calculate derived values
const calculateDerivedValues = (
  changedField: string,
  overallTotalProduct: number,
  initialValues: ReturnType<typeof getInitialValues>,
  application: FormValues
) => {
  const tankCount =
    changedField !== 'tankCount' && initialValues.tankSize
      ? Maths.divideSafeRound(overallTotalProduct, initialValues.tankSize)
      : initialValues.tankCount || 0;

  const overallRatePerAcre =
    changedField === 'overallRatePerAcre'
      ? initialValues.overallRatePerAcre || 0
      : Maths.divideSafeRound(
          overallTotalProduct,
          application.totalAppliedAcreage
        );

  const tankSize =
    changedField === 'tankSize'
      ? initialValues.tankSize || 0
      : Maths.divideSafeRound(
          overallTotalProduct,
          tankCount || initialValues.tankCount || 0
        );

  return { tankCount, overallRatePerAcre, tankSize };
};

// Helper function to calculate total cost for a product
export const calculateTotalCost = (
  product: Product | undefined,
  totalProduct: number,
  convertFn?: Function
): number => {
  if (!product || totalProduct <= 0) {
    return 0;
  }

  const priceBag = product.priceValue ?? product.averagePricePerUnit;

  if (!priceBag) {
    return 0;
  }

  const totalUnitIsUnitPrice = unitsAreSame(
    product.totalProductUnit,
    product.priceUnit || product.stdpackageunit
  );

  let totalCost = 0;
  if (totalUnitIsUnitPrice) {
    totalCost = Maths.multiplyRound(totalProduct, priceBag);
  } else {
    const totalProductConverted = calcConversions(
      totalProduct,
      product.totalProductUnit,
      product.priceUnit || product.stdpackageunit,
      totalUnitIsUnitPrice,
      product,
      convertFn
    );
    totalCost = Maths.multiplyRound(totalProductConverted, priceBag);
  }

  return isNumber(totalCost)
    ? Maths.parseFloatRound(totalCost, DEFAULT_ROUND, Decimal.ROUND_HALF_UP)
    : 0;
};

export const calculateCarrierTotals = (
  products: Product[],
  tankInformation: {
    overallRatePerAcre: number;
    tankCount: number;
    tankSize: number;
    overallTotalProduct: number;
  },
  convertFn?: Function
) => {
  const { overallTotalProduct, tankCount, tankSize } = tankInformation;
  // Filter out non-carrier solid products
  const nonCarrierProducts = getNonCarrierLiquidProducts(products);

  // Find the carrier product to get price information
  const carrierProduct = products.find(p => p.isCarrier);

  if (tankCount === 0 || tankSize === 0) {
    return {
      totalProduct: carrierProduct?.totalProduct ?? '',
      ratePerAcre: carrierProduct?.ratePerAcre ?? '',
      ratePerTank: carrierProduct?.ratePerTank ?? '',
      totalCost: 0
    };
  }

  const totalProduct = overallTotalProduct
    ? Maths.minusRound(
        overallTotalProduct,
        calculateTotalProductsByKey(nonCarrierProducts, 'totalProduct')
      )
    : 0;

  const ratePerAcre =
    (carrierProduct?.appliedAreaValue ?? 0) > 0
      ? Maths.divideSafeRound(
          totalProduct,
          carrierProduct?.appliedAreaValue ?? 0
        )
      : 0;

  const ratePerTank =
    tankCount > 0 ? Maths.divideSafeRound(totalProduct, tankCount) : 0;

  // Calculate total cost for carrier product
  const totalCost = calculateTotalCost(carrierProduct, totalProduct, convertFn);

  return {
    totalProduct,
    ratePerAcre,
    ratePerTank,
    totalCost
  };
};

// Helper function to calculate carrier product values
const calculateCarrierProduct = (
  product: Product,
  application: FormValues,
  calculatedValues: ReturnType<typeof calculateDerivedValues>,
  overallTotalProduct: number,
  convertFn?: Function
): Product => {
  const carrierTotals = calculateCarrierTotals(
    application.products,
    {
      overallRatePerAcre: calculatedValues.overallRatePerAcre,
      tankCount: calculatedValues.tankCount,
      tankSize: calculatedValues.tankSize,
      overallTotalProduct
    },
    convertFn
  );

  return {
    ...product,
    ratePerAcre: carrierTotals.ratePerAcre,
    ratePerTank: carrierTotals.ratePerTank,
    totalProduct: carrierTotals.totalProduct,
    totalCost: carrierTotals.totalCost
  };
};

// Helper function to calculate non-carrier product values
const calculateNonCarrierProduct = (
  product: Product,
  application: FormValues,
  calculatedValues: ReturnType<typeof calculateDerivedValues>,
  convertFn?: Function
): Product => {
  const convertedTotalProduct = calcConversions(
    product.totalProduct,
    product.totalProductUnit,
    'gallon',
    product.totalProductUnit === 'gallon',
    product,
    convertFn
  );

  const ratePerAcre = Maths.divideSafeRound(
    convertedTotalProduct,
    application.totalAppliedAcreage
  );

  const ratePerTank = Maths.divideSafeRound(
    convertedTotalProduct,
    calculatedValues.tankCount
  );

  const totalProduct =
    Maths.multiplyRound(ratePerAcre, application.totalAppliedAcreage) ||
    Maths.multiplyRound(ratePerTank, calculatedValues.tankCount);

  return {
    ...product,
    ratePerAcre: calcConversions(
      ratePerAcre,
      'gallon',
      product.ratePerAreaUnit,
      product.ratePerAreaUnit === 'gallon',
      product,
      convertFn
    ),
    ratePerTank: calcConversions(
      ratePerTank,
      'gallon',
      product.ratePerTankUnit,
      product.ratePerTankUnit === 'gallon',
      product,
      convertFn
    ),
    totalProduct: calcConversions(
      totalProduct,
      'gallon',
      product.totalProductUnit,
      product.totalProductUnit === 'gallon',
      product,
      convertFn
    )
  };
};

// Helper function to calculate product values
const calculateProductValues = (
  products: Product[],
  application: FormValues,
  calculatedValues: ReturnType<typeof calculateDerivedValues>,
  overallTotalProduct: number,
  convertFn?: Function
): Product[] => {
  return products.map(product => {
    if (!isLiquidProduct(product.stdunit) && !product.isCarrier) {
      return product;
    }

    if (product.isCarrier) {
      return calculateCarrierProduct(
        product,
        application,
        calculatedValues,
        overallTotalProduct,
        convertFn
      );
    }

    return calculateNonCarrierProduct(
      product,
      application,
      calculatedValues,
      convertFn
    );
  });
};

export const calculateApplicationValues = (
  application: FormValues,
  changedField: TankInfoFieldNames,
  value: number,
  convertFn?: Function
): FormValues => {
  const initialValues = getInitialValues(application, changedField, value);
  const overallTotalProduct = calculateOverallTotalProduct(
    changedField,
    initialValues,
    application
  );
  const calculatedValues = calculateDerivedValues(
    changedField,
    overallTotalProduct,
    initialValues,
    application
  );

  const products = calculateProductValues(
    application.products,
    application,
    calculatedValues,
    overallTotalProduct,
    convertFn
  );

  return {
    ...application,
    products,
    totalProduct: overallTotalProduct,
    tankCount: calculatedValues.tankCount || undefined,
    overallRatePerAcre: calculatedValues.overallRatePerAcre,
    tankSize: calculatedValues.tankSize || undefined
  };
};
